import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import InputComponent from "../../../components/Tenant/InputComponent/InputComponent";
import { HiOutlinePlus } from "react-icons/hi";
import { AiOutlineDelete, AiOutlineEdit, AiOutlineTeam } from "react-icons/ai";
import { toast } from "react-hot-toast";
import {
  useCreateApprovalWorkflowMutation,
  useGetAllApprovalWorkflowsMutation,
  useUpdateApprovalWorkflowMutation,
  useDeleteApprovalWorkflowMutation,
} from "../../../features/tenant/approvalWorkflow/approvalWorkflow.Api";
import {
  useGetAllTenantUserMutation,
} from "../../../features/tenant/tenantAuth/tenantAuthApi";

const ApprovalWorkflow = () => {
  const token = sessionStorage.getItem("access_token");
  const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenant_id = vendorData?.tenant_id;

  // ── Workflow create/edit modal ────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState("add");
  const [editId, setEditId] = useState(null);
  const [entityType, setEntityType] = useState("");
  const [levels, setLevels] = useState([{ level: 1, role_name: "", user_list: [] }]);
  const [isActive, setIsActive] = useState(true);

  // ── Assign users modal ────────────────────────────────────────────────────────
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignWorkflow, setAssignWorkflow] = useState(null);
  const [assignLevels, setAssignLevels] = useState([]);
  const [activeAssignLevel, setActiveAssignLevel] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [panelUsers, setPanelUsers] = useState([]);

  // ── Data ──────────────────────────────────────────────────────────────────────
  const [workflows, setWorkflows] = useState([]);

  // ── API hooks ──────────────────────────────────────────────────────────────────
  const [getAllApprovalWorkflows, { isLoading }] = useGetAllApprovalWorkflowsMutation();
  const [createApprovalWorkflow]                = useCreateApprovalWorkflowMutation();
  const [updateApprovalWorkflow]                = useUpdateApprovalWorkflowMutation();
  const [deleteApprovalWorkflow]                = useDeleteApprovalWorkflowMutation();
  const [getAllTenantUser]                       = useGetAllTenantUserMutation();

  // ── Fetch on mount ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchWorkflows();
    fetchPanelUsers();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const res = await getAllApprovalWorkflows({ token, tenant_id }).unwrap();
      if (res?.status) setWorkflows(res.body || []);
    } catch { toast.error("Failed to load workflows"); }
  };

  const fetchPanelUsers = async () => {
    try {
      const res = await getAllTenantUser({
        token, tenant_id,
        body: { name: "", designation: "", email: "", mobile: "", limit: 500, offset: 0 },
      }).unwrap();
      if (res?.status) setPanelUsers(res.body?.users || []);
    } catch {}
  };

  // ── Level builder helpers ─────────────────────────────────────────────────────
  const addLevel = () =>
    setLevels((prev) => [...prev, { level: prev.length + 1, role_name: "", user_list: [] }]);

  const removeLevel = (idx) =>
    setLevels((prev) =>
      prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, level: i + 1 }))
    );

  const updateLevel = (idx, field, value) =>
    setLevels((prev) =>
      prev.map((l, i) => (i !== idx ? l : { ...l, [field]: value }))
    );

  // ── Open create/edit ──────────────────────────────────────────────────────────
  const handleOpenAdd = () => {
    setAction("add"); setEditId(null);
    setEntityType(""); setLevels([{ level: 1, role_name: "", user_list: [] }]);
    setIsActive(true); setShowModal(true);
  };

  const handleOpenEdit = (wf) => {
    setAction("edit"); setEditId(wf.id);
    setEntityType(wf.entity_type);
    setLevels((wf.level_json || []).map((l) => ({ ...l, user_list: l.user_list || [] })));
    setIsActive(wf.is_active); setShowModal(true);
  };

  // ── Save workflow ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!entityType.trim()) return toast.error("Entity type is required");
    if (levels.some((l) => !l.role_name?.trim())) return toast.error("Enter a role name for each level");
    const payload = { entity_type: entityType.trim().toLowerCase(), level_json: levels, is_active: isActive };
    try {
      if (action === "add") {
        const res = await createApprovalWorkflow({ token, tenant_id, data: payload }).unwrap();
        if (res?.status) { toast.success("Workflow created"); fetchWorkflows(); setShowModal(false); }
        else toast.error(res?.message || "Failed to create");
      } else {
        const res = await updateApprovalWorkflow({ token, tenant_id, id: editId, data: payload }).unwrap();
        if (res?.status) { toast.success("Workflow updated"); fetchWorkflows(); setShowModal(false); }
        else toast.error(res?.message || "Failed to update");
      }
    } catch (err) { toast.error(err?.data?.message || "Something went wrong"); }
  };

  // ── Delete workflow ───────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this workflow?")) return;
    try {
      const res = await deleteApprovalWorkflow({ token, tenant_id, id }).unwrap();
      if (res?.status) { toast.success("Workflow deactivated"); fetchWorkflows(); }
      else toast.error(res?.message || "Failed to delete");
    } catch { toast.error("Something went wrong"); }
  };

  // ── Open assign users modal ───────────────────────────────────────────────────
  const handleOpenAssign = (wf) => {
    setAssignWorkflow(wf);
    setAssignLevels((wf.level_json || []).map((l) => ({ ...l, user_list: l.user_list || [] })));
    setActiveAssignLevel(0);
    setUserSearch("");
    setShowAssignModal(true);
  };

  // ── Toggle a user in the active level's user_list ─────────────────────────────
  const toggleUser = (userId) => {
    setAssignLevels((prev) =>
      prev.map((l, i) => {
        if (i !== activeAssignLevel) return l;
        const already = l.user_list.includes(userId);
        return {
          ...l,
          user_list: already
            ? l.user_list.filter((id) => id !== userId)
            : [...l.user_list, userId],
        };
      })
    );
  };

  // ── Save user assignments ─────────────────────────────────────────────────────
  const handleSaveAssign = async () => {
    try {
      const res = await updateApprovalWorkflow({
        token, tenant_id,
        id: assignWorkflow.id,
        data: {
          entity_type: assignWorkflow.entity_type,
          level_json: assignLevels,
          is_active: assignWorkflow.is_active,
        },
      }).unwrap();
      if (res?.status) {
        toast.success("Approvers assigned successfully");
        fetchWorkflows();
        setShowAssignModal(false);
      } else {
        toast.error(res?.message || "Failed to save");
      }
    } catch (err) { toast.error(err?.data?.message || "Something went wrong"); }
  };

  // ── Filtered users for search ─────────────────────────────────────────────────
  const filteredUsers = panelUsers.filter(
    (u) =>
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.designation?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const currentLevelUsers = assignLevels[activeAssignLevel]?.user_list || [];

  // ── Table columns ─────────────────────────────────────────────────────────────
  const columns = [
    { name: "S.No", cell: (row, idx) => idx + 1, width: "70px" },
    {
      name: "Entity Type",
      selector: (row) => row.entity_type,
      cell: (row) => <span className="font-medium capitalize">{row.entity_type.replace(/_/g, " ")}</span>,
      sortable: true,
    },
    {
      name: "Levels",
      cell: (row) => (
        <div className="flex flex-col gap-1 py-1">
          {(row.level_json || []).map((l) => (
            <span key={l.level} className="text-xs bg-gray-100 rounded px-2 py-0.5">
              L{l.level}: {l.role_name} — <strong>{l.user_list?.length || 0}</strong> user(s)
            </span>
          ))}
        </div>
      ),
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${row.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
          {row.is_active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => handleOpenAssign(row)}
            className="flex justify-center items-center rounded bg-purple-600 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition hover:bg-purple-700"
          >
            <AiOutlineTeam size={14} className="mr-1" /> Assign
          </button>
          <button
            onClick={() => handleOpenEdit(row)}
            className="flex justify-center items-center rounded bg-blue-500 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition hover:bg-blue-600"
          >
            <AiOutlineEdit size={14} className="mr-1" /> Edit
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="flex justify-center items-center rounded bg-red-500 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow transition hover:bg-red-600"
          >
            <AiOutlineDelete size={14} className="mr-1" /> Delete
          </button>
        </div>
      ),
      minWidth: "280px",
    },
  ];

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full">
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            Approval Workflows
            <LinksComponent />
          </div>

          <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
            <button onClick={handleOpenAdd} className="btn btn-outline-primary">
              <HiOutlinePlus className="inline mr-1" size={16} /> Add Workflow
            </button>
          </div>

          <div className="p-3">
            <div className="p-3 w-full bg-white shadow rounded-lg">
              <DataTableComponent
                columns={columns}
                data={workflows}
                isLoading={isLoading}
                totalRows={workflows.length}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Create / Edit Modal ─────────────────────────────────────────────────── */}
      <ModalComponent size="lg" show={showModal} setShow={setShowModal}
        title={action === "add" ? "Create Approval Workflow" : "Edit Approval Workflow"}>
        <div className="flex flex-col gap-4 p-4">
          <div>
            <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">Entity Type *</label>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              disabled={action === "edit"}
            >
              <option value="">Select entity type</option>
              <option value="cash_redemption">cash_redemption</option>
              <option value="gift_redemption">gift_redemption</option>
              <option value="voucher_redemption">voucher_redemption</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">Must be unique. Use lowercase with underscores.</p>
          </div>

          <div>
            <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-2">Approval Levels *</label>
            <div className="flex flex-col gap-2">
              {levels.map((lvl, idx) => (
                <div key={idx} className="flex items-center gap-2 border rounded p-2 bg-gray-50">
                  <span className="text-xs font-bold text-gray-500 w-14">Level {lvl.level}</span>
                  <input
                    type="text"
                    className="flex-1 border rounded px-2 py-1.5 text-sm"
                    placeholder="Role label (e.g. Manager)"
                    value={lvl.role_name}
                    onChange={(e) => updateLevel(idx, "role_name", e.target.value)}
                  />
                  {levels.length > 1 && (
                    <button onClick={() => removeLevel(idx)} className="text-red-400 hover:text-red-600 text-xs px-1">✕</button>
                  )}
                </div>
              ))}
            </div>
            <button onClick={addLevel} className="mt-2 text-sm text-indigo-600 hover:text-indigo-800">+ Add Level</button>
            <p className="text-xs text-gray-400 mt-1">After saving, click <strong>Assign</strong> to assign panel users to each level.</p>
          </div>

          <div className="flex items-center gap-3">
            <label className="block text-sm font-medium capitalize leading-6 text-gray-900">Active</label>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-4 h-4 cursor-pointer" />
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} className="btn btn-outline-primary">
              {action === "add" ? "Create Workflow" : "Save Changes"}
            </button>
          </div>
        </div>
      </ModalComponent>

      {/* ── Assign Users Modal ──────────────────────────────────────────────────── */}
      <ModalComponent size="lg" show={showAssignModal} setShow={setShowAssignModal}
        title={`Assign Approvers — ${assignWorkflow?.entity_type?.replace(/_/g, " ")}`}>
        <div className="flex flex-col gap-4 p-4">

          {/* Level tabs */}
          <div className="flex gap-2 border-b pb-2 flex-wrap">
            {assignLevels.map((l, i) => (
              <button
                key={i}
                onClick={() => { setActiveAssignLevel(i); setUserSearch(""); }}
                className={`px-4 py-1.5 rounded text-sm font-medium transition ${
                  activeAssignLevel === i
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Level {l.level} — {l.role_name || "Unnamed"}
                <span className="ml-1 text-xs opacity-75">({l.user_list?.length || 0})</span>
              </button>
            ))}
          </div>

          {/* Search */}
          <input
            type="search"
            className="w-full border rounded px-3 py-1.5 text-sm"
            placeholder="Search by name, email or designation..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />

          {/* User list with checkboxes */}
          <div className="overflow-y-auto max-h-72 border rounded divide-y">
            {filteredUsers.length === 0 ? (
              <p className="text-sm text-gray-400 p-4 text-center">No users found</p>
            ) : (
              filteredUsers.map((u) => {
                const checked = currentLevelUsers.includes(u.id);
                return (
                  <label
                    key={u.id}
                    className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50 ${checked ? "bg-purple-50" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleUser(u.id)}
                      className="w-4 h-4 accent-purple-600 cursor-pointer"
                    />
                    <div className="flex flex-col flex-1">
                      <span className="text-sm font-medium text-gray-800">{u.name}</span>
                      <span className="text-xs text-gray-400">
                        {u.email}{u.designation ? ` · ${u.designation}` : ""}
                      </span>
                    </div>
                    {checked && <span className="text-xs text-purple-600 font-semibold">✓ Assigned</span>}
                  </label>
                );
              })
            )}
          </div>

          <p className="text-xs text-gray-500">
            <strong>{currentLevelUsers.length}</strong> user(s) assigned to Level {assignLevels[activeAssignLevel]?.level} ({assignLevels[activeAssignLevel]?.role_name})
          </p>

          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAssignModal(false)} className="px-4 py-2 border rounded text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSaveAssign} className="px-4 py-2 rounded text-sm text-white bg-purple-600 hover:bg-purple-700">
              Save Assignments
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  );
};

export default ApprovalWorkflow;
